import { showSaveFilePicker } from 'file-system-access';
import i18next from 'i18next';

import type { TerritoryCode } from '~/lib/territories/territories';

import type {
  BenchmarkDataType,
  BenchmarksDataResponse,
  CampaignBenchmark,
} from './types';
import { toTitleCase } from './utils';

const formatBenchmarkData = (data: BenchmarkDataType): CampaignBenchmark => {
  return {
    id: data.id,
    market: data.country_code,
    artistIds: data.artist_id ? [data.artist_id] : [],
    objective: data.objective,
    platform: data.social_platform,
    type: data.data_level,
    CPM: data.cpm,
    CPV: data.cpv,
    CPC: data.cpc,
    VTR: data.vtr,
    CTR: data.ctr,
    engagementRate: data.engagement_rate,
  };
};

export const getBenchmarks = async ({
  uaid,
  objective,
  territoryCodes,
  platforms,
}: {
  uaid?: string;
  objective?: string;
  territoryCodes?: TerritoryCode[];
  platforms?: string[];
}) => {
  if (!uaid) {
    throw new Error('UAID is required');
  }

  if (!objective) {
    throw new Error('Marketing objective is required');
  }

  try {
    const params = new URLSearchParams();

    params.set('artist_id', uaid);

    if (objective) {
      params.set('objective', toTitleCase(objective));
    }

    if (territoryCodes && territoryCodes.length > 0) {
      params.set('country_code', territoryCodes.join(','));
    }

    if (platforms && platforms.length > 0) {
      params.set('platform', platforms.map(toTitleCase).join(','));
    }

    const response = await fetch(
      `/api/mixone/api/v1/campaign_planner/benchmarks?${params.toString()}`,
    );

    if (!response.ok) {
      if (response.status === 404) {
        // Mixone API currently returns 404 for benchmarks not found
        // We want to handle this gracefully and return an empty array instead of throwing an error
        console.warn('Benchmarks not found (404). Returning an empty array.');

        return [];
      }

      throw new Error(`Failed to fetch benchmarks: ${response.statusText}`);
    }

    const data: BenchmarksDataResponse = await response.json();

    return data.data?.length ? data.data.map(formatBenchmarkData) : [];
  } catch (error) {
    console.error('Error fetching benchmarks:', error);

    throw error;
  }
};

export async function exportBenchmarksCSV({
  artistName,
  objective,
  benchmarksData,
}: {
  artistName: string;
  objective?: string;
  benchmarksData: CampaignBenchmark[];
}): Promise<boolean> {
  let fileHandle;

  try {
    fileHandle = await showSaveFilePicker({
      _preferPolyfill: false,
      suggestedName: `${artistName}${objective ? '-' + objective : ''}-campaign-benchmarks.csv`,
      types: [{ description: 'CSV Files', accept: { 'text/csv': ['.csv'] } }],
    });
  } catch {
    return false;
  }

  let stream: FileSystemWritableFileStream | null = null;

  try {
    stream = await fileHandle.createWritable();

    const headers = [
      'uaid',
      'Artist Name',
      'Market',
      'Market Code',
      'Objective',
      'Platform',
      'CPM',
      'CPV',
      'CPC',
      'VTR',
      'CTR',
      'Engagement Rate',
    ];

    const csvRows = [headers.join(',')];

    for (const row of benchmarksData) {
      csvRows.push(
        [
          row.artistIds?.join('|') ?? '',
          artistName,
          row.market
            ? i18next.t(`names.${row.market}`, { ns: 'territories' })
            : '',
          row.market,
          row.objective ?? '',
          row.platform ?? '',
          row.CPM ?? '',
          row.CPV ?? '',
          row.CPC ?? '',
          row.VTR ?? '',
          row.CTR ?? '',
          row.engagementRate ?? '',
        ].join(','),
      );
    }

    await stream.write(csvRows.join('\n'));

    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      if (stream) {
        await stream.abort();
      }

      return false;
    }

    throw error;
  } finally {
    if (stream) {
      await stream.close();
    }
  }
}
